import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";
import { useFetch } from "../lib/api";
import { ProductGrid } from "../components/site/ProductCard";
import { Seo } from "../components/site/Seo";

const SORTS = [["newest", "Newest"], ["price_asc", "Price: Low to High"], ["price_desc", "Price: High to Low"], ["popular", "Popular"], ["sale", "Sale"]];

function ChipGroup({ label, options, value, onChange, testid }) {
  const sel = value ? value.split(",") : [];
  const toggle = (o) => onChange((sel.includes(o) ? sel.filter((x) => x !== o) : [...sel, o]).join(","));
  if (!options?.length) return null;
  return (
    <div>
      <p className="label">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o} type="button" onClick={() => toggle(o)} data-testid={`${testid}-${o.toLowerCase().replace(/\s+/g, "-")}`}
            className={`chip ${sel.includes(o) ? "chip-active" : "hover:border-white/40"}`}>{o}</button>
        ))}
      </div>
    </div>
  );
}

function Filters({ params, set, facets, preset }) {
  const g = (k) => params.get(k) || "";
  return (
    <div className="space-y-7">
      <div>
        <p className="label">Search</p>
        <input className="field" value={g("q")} onChange={(e) => set("q", e.target.value)} placeholder="Search products" data-testid="shop-search-input" />
      </div>
      {!preset.category && <ChipGroup label="Category" options={facets?.categories} value={g("category")} onChange={(v) => set("category", v)} testid="filter-category" />}
      {!preset.gender && <ChipGroup label="Gender" options={["Men", "Women", "Unisex"]} value={g("gender")} onChange={(v) => set("gender", v)} testid="filter-gender" />}
      <ChipGroup label="Brand" options={facets?.brands} value={g("brand")} onChange={(v) => set("brand", v)} testid="filter-brand" />
      <ChipGroup label="Size (in stock)" options={facets?.sizes} value={g("size")} onChange={(v) => set("size", v)} testid="filter-size" />
      <ChipGroup label="Colour" options={facets?.colours} value={g("colour")} onChange={(v) => set("colour", v)} testid="filter-colour" />
      <div>
        <p className="label">Price (R)</p>
        <div className="flex gap-2">
          <input type="number" min="0" className="field" placeholder="Min" value={g("min_price")} onChange={(e) => set("min_price", e.target.value)} data-testid="filter-min-price" />
          <input type="number" min="0" className="field" placeholder="Max" value={g("max_price")} onChange={(e) => set("max_price", e.target.value)} data-testid="filter-max-price" />
        </div>
      </div>
      <div>
        <p className="label">Availability</p>
        <select className="field" value={g("availability")} onChange={(e) => set("availability", e.target.value)} data-testid="filter-availability">
          <option value="">All</option><option value="in_stock">In stock</option><option value="out_of_stock">Out of stock</option>
        </select>
      </div>
    </div>
  );
}

export default function Shop({ title = "Shop All", eyebrow = "The collection", preset = {}, description }) {
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const set = (k, v) => {
    const next = new URLSearchParams(params);
    v ? next.set(k, v) : next.delete(k);
    setParams(next, { replace: true });
  };
  const query = useMemo(() => {
    const q = new URLSearchParams(params);
    Object.entries(preset).forEach(([k, v]) => q.set(k, v));
    q.set("limit", "100");
    return `/products?${q.toString()}`;
  }, [params, preset]);
  const { data, loading } = useFetch(query);
  const activeCount = [...params.keys()].filter((k) => k !== "sort").length;
  return (
    <div className="container-x py-10 sm:py-16">
      <Seo title={title} description={description || `Shop ${title} at Mwezzy Footwear. Nationwide courier delivery in South Africa.`} />
      <p className="eyebrow">{eyebrow}</p>
      <h1 className="font-display text-6xl sm:text-8xl leading-none mt-2" data-testid="shop-title">{title}</h1>
      <div className="flex items-center justify-between gap-3 mt-8 pb-5 border-b border-white/10">
        <p className="text-sm text-zinc-400" data-testid="shop-result-count">{loading ? "Loading..." : `${data?.total ?? 0} products`}</p>
        <div className="flex gap-2">
          <button onClick={() => setOpen(true)} className="lg:hidden chip flex items-center gap-2" data-testid="shop-filter-button">
            <SlidersHorizontal size={16} /> Filters {activeCount > 0 && `(${activeCount})`}
          </button>
          <select className="field !w-auto !min-h-[44px]" value={params.get("sort") || "newest"} onChange={(e) => set("sort", e.target.value)} data-testid="shop-sort-select">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="grid lg:grid-cols-[260px_1fr] gap-10 mt-8">
        <aside className="hidden lg:block"><Filters params={params} set={set} facets={data?.facets} preset={preset} /></aside>
        <div>
          {activeCount > 0 && <button onClick={() => setParams({})} className="text-xs uppercase tracking-widest text-gold mb-4 flex items-center gap-1" data-testid="shop-clear-filters"><X size={14} /> Clear filters</button>}
          {!loading && data?.items?.length === 0 && <p className="text-zinc-400 py-20 text-center" data-testid="shop-empty">No products match your filters.</p>}
          <ProductGrid items={data?.items || []} />
        </div>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="bg-[#0a0a0c] border-white/10 max-h-[85vh] overflow-y-auto rounded-t-3xl">
          <SheetTitle className="font-display text-4xl text-white mb-6">Filters</SheetTitle>
          <Filters params={params} set={set} facets={data?.facets} preset={preset} />
          <button onClick={() => setOpen(false)} className="btn-gold w-full mt-8 sticky bottom-0" data-testid="shop-filter-apply">Show {data?.total ?? 0} results</button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
